/**
 * v3.17-1240 — Persistent Operational Notification Tray.
 *
 * A stackable, always-visible alert surface for CRITICAL business events
 * (New Credit Application, ACH Auth, Credit Card Auth, New Client, Hub
 * Message, Ghost Card assigned, Appointment starting soon / overdue,
 * Transfer Call, Support Request, future critical Ghost events).
 *
 * Contract with the backend (see services/persistent_notifications.py):
 *   • Notifications flow through the existing SSE stream.
 *   • Backend enrichment stamps `persistent`, `severity`, `sound_key`.
 *   • This tray owns any row where `persistent === true`.
 *   • Non-persistent rows continue to use sonner toasts (NotificationCenter).
 *
 * Sound engine (Hybrid — user directive Q4c):
 *   • Immediately play at 100% volume on arrival.
 *   • Persistent card remains on screen with a manual Dismiss.
 *   • If not dismissed within 60s, replay at 40% volume, and every 60s
 *     thereafter until dismissed.
 *   • Critical events use a different sound tone than informational
 *     (sound_key = "critical" | "info").
 *
 * Stacking:
 *   • New alerts stack under existing ones (do not replace).
 *   • Each is dismissed individually.
 *
 * Dismissal:
 *   • Dismiss ONLY acknowledges. It does not complete the underlying
 *     Ghost work item. The Ghost dock remains the work queue; this
 *     tray is the alarm.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Bell, ArrowRight } from 'lucide-react';
import api from '../lib/api';


// v3.17-1240 — persistent notification event registry (mirrors backend).
const PERSISTENT_TYPES = new Set([
  'credit_application_submitted',
  'ach_authorization_submitted',
  'credit_card_authorization_submitted',
  'new_client_registered',
  'hub_message_received',
  'ghost_card_assigned',
  'appointment_starting_soon',
  'appointment_overdue',
  'transfer_call_incoming',
  'support_request_incoming',
  'critical_ghost_event',
]);


// Audio engine — WebAudio short beeps at two tones (critical vs info).
const makeBeep = (freq, durationSec = 0.6, vol = 1.0) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(vol * 0.35, ctx.currentTime + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationSec);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durationSec);
    setTimeout(() => { try { ctx.close(); } catch (_) { /* noop */ } }, durationSec * 1000 + 200);
  } catch (_e) { /* silent */ }
};

const playSound = (key, volume = 1.0) => {
  // Critical: three-tone stab. Info: single higher chime.
  if (key === 'critical') {
    makeBeep(880, 0.16, volume);
    setTimeout(() => makeBeep(660, 0.18, volume), 180);
    setTimeout(() => makeBeep(520, 0.28, volume), 380);
  } else {
    makeBeep(1200, 0.35, volume);
  }
};


const REPLAY_INTERVAL_MS = 60_000;    // 60s per user directive Q4c
const REPLAY_VOLUME      = 0.4;       // 40% per user directive


export const PersistentNotificationTray = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);          // active persistent rows
  const timersRef = useRef({});                    // id → interval handle
  const seenIdsRef = useRef(new Set());
  const esRef = useRef(null);

  const isPersistent = useCallback((n) => {
    if (n?.persistent === true) return true;
    if (n?.persistent === false) return false;
    return PERSISTENT_TYPES.has(n?.type);
  }, []);

  const startReplay = useCallback((n) => {
    const key = n.sound_key || (n.severity === 'critical' ? 'critical' : 'info');
    const handle = setInterval(() => playSound(key, REPLAY_VOLUME), REPLAY_INTERVAL_MS);
    timersRef.current[n.id] = handle;
  }, []);

  const dismiss = useCallback(async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    const t = timersRef.current[id];
    if (t) { clearInterval(t); delete timersRef.current[id]; }
    try { await api.post(`/notifications/${id}/read`); } catch (_e) { /* silent */ }
  }, []);

  const addIfPersistent = useCallback((n) => {
    if (!n || !isPersistent(n)) return;
    if (!n.id || seenIdsRef.current.has(n.id)) return;
    seenIdsRef.current.add(n.id);
    setItems((prev) => [n, ...prev].slice(0, 20));
    // Immediate 100% sound on arrival.
    playSound(n.sound_key || 'critical', 1.0);
    // Set the 60s replay ticker.
    startReplay(n);
  }, [isPersistent, startReplay]);

  // Prime with any unread persistent rows on mount (in case the user
  // reloads with a queue already sitting in Mongo).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/notifications', { params: { limit: 50, unread_only: true } });
        if (cancelled) return;
        const rows = data?.notifications || [];
        rows.filter(isPersistent).forEach(addIfPersistent);
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [addIfPersistent, isPersistent]);

  // SSE stream — parallel to NotificationCenter; identical URL.
  // v3.21-1729 · The old `localStorage.getItem('token')` read used the
  // wrong storage key ('token' vs 'access_token') and always returned
  // null after the v1684 migration, silently disabling this SSE
  // stream.  Fixed to read from the same authoritative locations as
  // the axios client.
  useEffect(() => {
    const token = sessionStorage.getItem('access_token')
      || localStorage.getItem('access_token');
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!token || !backend) return undefined;
    const url = `${backend}/api/notifications/stream?token=${encodeURIComponent(token)}`;
    try {
      const es = new EventSource(url);
      esRef.current = es;
      es.addEventListener('notification', (evt) => {
        try {
          const n = JSON.parse(evt.data);
          addIfPersistent(n);
        } catch (_e) { /* ignore parse errors */ }
      });
      es.onerror = () => { /* auto-reconnects */ };
      return () => { try { es.close(); } catch (_e) { /* noop */ } };
    } catch (_e) { return undefined; }
  }, [addIfPersistent]);

  // Cleanup all replay timers on unmount.
  useEffect(() => () => {
    Object.values(timersRef.current).forEach((h) => clearInterval(h));
    timersRef.current = {};
  }, []);

  if (items.length === 0) return null;

  return (
    <div
      data-testid="persistent-notification-tray"
      className="fixed z-[9999] right-4 top-4 flex flex-col gap-2 max-w-sm"
    >
      {items.map((n) => (
        <div
          key={n.id}
          data-testid={`persistent-notification-${n.id}`}
          className="rounded-lg border-l-4 border-red-500 bg-white shadow-2xl
                     px-4 py-3 w-96 animate-pulse-once"
        >
          <div className="flex items-start gap-2">
            <div className="mt-0.5 h-8 w-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <Bell className="h-4 w-4 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-red-600">
                  Critical · Requires acknowledgement
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(n.id)}
                  data-testid={`persistent-notification-dismiss-${n.id}`}
                  className="h-6 w-6 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center"
                  title="Acknowledge (does not complete the item)"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="text-sm font-semibold text-slate-900 mt-0.5 leading-snug">
                {n.title || 'Operational alert'}
              </div>
              {n.client_name && (
                <div className="text-xs text-slate-600 mt-0.5">
                  <span className="text-slate-400">Client:</span>{' '}
                  <span className="font-medium">{n.client_name}</span>
                </div>
              )}
              {n.message && (
                <div className="text-xs text-slate-700 mt-1 line-clamp-3">
                  {n.message}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 mt-2">
                <div className="text-[10px] text-slate-400">
                  {n.created_at ? new Date(n.created_at).toLocaleTimeString() : ''}
                </div>
                {n.link && (
                  <button
                    type="button"
                    onClick={() => { navigate(n.link); dismiss(n.id); }}
                    data-testid={`persistent-notification-open-${n.id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-red-600 hover:text-red-700"
                  >
                    Open
                    <ArrowRight className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PersistentNotificationTray;
